// =============================================================================
// SiteListDialog.jsx
//
// Popup para seleccionar quais sites incluir num server.
//
// PORQUÊ está num ficheiro separado e é renderizado no TrackerPage?
//   O MUI renderiza cada Dialog num React Portal (nó directo no <body>).
//   Quando este Dialog fecha, a animação de saída dispara eventos de clique
//   que o MUI interpreta como "backdrop click do Dialog pai aberto".
//   Resultado: o TrackerModal fechava automaticamente ao confirmar os sites.
//
//   Solução: este Dialog vive no TrackerPage, ao mesmo nível que o TrackerModal.
//   Os dois Dialogs são irmãos independentes — o fecho de um não afecta o outro.
//
// Fluxo:
//   1. Utilizador clica "Site List" num ServerCard dentro do TrackerModal
//   2. TrackerModal chama props.onOpenSiteList(server, updateServerFn)
//   3. TrackerPage guarda o server, a callback, e abre este Dialog
//   4. Utilizador selecciona sites e clica "Confirm"
//   5. TrackerPage chama updateServerFn(selectedSites) → actualiza o form
//   6. TrackerPage fecha este Dialog
//
// Props:
//   open      → boolean
//   server    → { omcId, tac, allSites, selectedSites } | null
//   onClose   → fechar sem guardar
//   onConfirm → callback(selectedSites: string[])
// =============================================================================

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, IconButton, Box,
  Divider, List, ListItem, ListItemButton, ListItemText, ListItemIcon,
  Checkbox, InputAdornment, Paper, Chip,
} from '@mui/material';
import CloseIcon  from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

const SiteListDialog = ({ open, server, onClose, onConfirm }) => {
  const [search,   setSearch]   = useState('');
  const [selected, setSelected] = useState([]);

  // Inicializar selecção quando o popup abre.
  // Usa a selecção guardada se existir; caso contrário selecciona tudo (opt-out).
  useEffect(() => {
    if (!open || !server) return;
    const all     = server.allSites      ?? [];
    const current = server.selectedSites ?? [];
    setSelected(current.length > 0 ? [...current] : [...all]);
    setSearch('');
  }, [open]);

  // Durante a animação de fecho o server pode ser null
  if (!server) return null;

  const allSites = server.allSites ?? [];
  const filtered = allSites.filter((s) =>
    s.toLowerCase().includes(search.toLowerCase())
  );

  // Toggle: seleccionado → remove; não seleccionado → adiciona
  const toggle = (site) => {
    setSelected((prev) =>
      prev.includes(site)
        ? prev.filter((s) => s !== site)
        : [...prev, site]
    );
  };

  const selectionColor =
    selected.length === allSites.length ? 'success' :
    selected.length === 0               ? 'error'   : 'warning';

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      scroll="paper"
      // zIndex superior ao TrackerModal para garantir que fica por cima
      sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Box sx={{ fontWeight: 700, fontSize: '1.1rem' }}>Site List</Box>
          <Typography variant="caption" color="text.secondary" component="div">
            Server: <strong>{server.omcId}</strong> · TAC: <strong>{server.tac}</strong>
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>

        {/* Barra de pesquisa + contador */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <TextField
            size="small"
            placeholder="Search sites…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          {/* Chip de contagem: verde = todos, vermelho = nenhum, laranja = parcial */}
          <Chip
            label={`${selected.length} / ${allSites.length}`}
            size="small"
            color={selectionColor}
          />
        </Box>

        {/* Selecção rápida */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Button size="small" variant="text" onClick={() => setSelected([...allSites])}>
            Select All
          </Button>
          <Button size="small" variant="text" color="error" onClick={() => setSelected([])}>
            Deselect All
          </Button>
        </Box>

        {/* Lista de sites — MUI v6: ListItemButton em vez de <ListItem button> */}
        <Paper variant="outlined" sx={{ maxHeight: 380, overflowY: 'auto' }}>
          <List dense disablePadding>
            {filtered.length === 0 && (
              <ListItem>
                <ListItemText secondary="No sites match the search." />
              </ListItem>
            )}
            {filtered.map((site) => {
              const checked = selected.includes(site);
              return (
                <ListItemButton
                  key={site}
                  onClick={() => toggle(site)}
                  sx={{ '&:hover': { bgcolor: 'action.selected' } }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox edge="start" checked={checked} size="small" disableRipple />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        sx={{
                          color:          checked ? 'text.primary' : 'text.disabled',
                          textDecoration: checked ? 'none'         : 'line-through',
                        }}
                      >
                        {site}
                      </Typography>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>

      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button
          onClick={() => onConfirm(selected)}
          variant="contained"
          disabled={selected.length === 0}
        >
          Confirm Selection
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SiteListDialog;